import { ISceneNode } from "../scene/ISceneNode";
import { Vector3, Object3D, Mesh } from "three";
import { Signal } from "../utils/signal/Signal";
import { Config } from "../config/Config";
import { Scene } from "../scene/Scene";
import { BuildingGeometry } from "./BuildingGeometry";
import { BuildingWall } from "./wall/BuildingWall";
import { ISceneAddon, SceneAddonType } from "../scene/addon/ISceneAddon";
import { Main } from "../Main";
import { AddonManager } from "../scene/addon/AddonManager";
import { WallAddon } from "../scene/addon/WallAddon";
import { RidgeVent } from "../scene/extras/RidgeVent";
import { Roof } from "./roof/Roof";
import { InsetBayManager } from "../scene/extras/insetbay/InsetBayManager";
import { Wainscot } from "../scene/extras/wainscot/Wainscot";
import { LeanToManager } from "../scene/extras/leanto/LeanToManager";
import { MaterialManager } from "../material/MaterialManager";
import { ConcreteSlab } from "../scene/extras/ConcreteSlab";
import { SupportBeam } from "../scene/extras/supportbeam/SupportBeam";
import { BuildingTrim } from "./trim/BuildingTrim";
import { IAddonSetting } from "../config/IProjectSettings";
import { WarningWindow } from "../ui/WarningWindow";

export type WallSide = "left" | "right" | "front" | "back";

export class Building implements ISceneNode
{
	public static WALLS: {[key: string]: WallSide} = {
		LEFT: "left",
		RIGHT: "right",
		FRONT: "front",
		BACK: "back"
	};

	public static NORMALS: {
		[key: string]: Vector3
	} = {
		front: new Vector3(0, 0, 1),
		back: new Vector3(0, 0, -1),
		left: new Vector3(-1, 0, 0),
		right: new Vector3(1, 0, 0)
	};

	public static ROOF_LEFT = "roof_left";
	public static ROOF_RIGHT = "roof_right";

	public signals = {
		addonsChange: new Signal()
	};

	public name = "building";
	public id = "building";

	private _geometry : BuildingGeometry;

	private _config:Config;

	private _scene3D:Object3D;
	private _scene:Scene;

	private _wall : BuildingWall;
	private _roof:Roof;
	private _addons: {
		[wallId: string]: ISceneAddon[]
	};

	// extras
	private _ridgeVent:RidgeVent;
	private _wainscot:Wainscot;
	private _concreteSlab:ConcreteSlab;
	private _insetBayManager:InsetBayManager;
	private _leanToManager:LeanToManager;
	private _supportBeam:SupportBeam;
	private _trim:BuildingTrim;

	private _materials : MaterialManager;

	public _meshes:{
		floor : Mesh;
	};

	constructor(config:Config, scene:Scene)
	{
		this._config = config;
		this._scene = scene;

		this._addons = {};
		this._addons[Building.WALLS.LEFT] = [];
		this._addons[Building.WALLS.RIGHT] = [];
		this._addons[Building.WALLS.FRONT] = [];
		this._addons[Building.WALLS.BACK] = [];
	}

	// will get called when added to scene
	private init(config?: any)
	{
		// needs to be added first (before extras are created)
		// in case extras use metrics
		this.addListeners();

		this.createMeshes(config);
		this._roof = new Roof(this);
		this._wall = new BuildingWall(this);

		this.createTrims();
		this.createExtras();
	}

	private addListeners()
	{
		var buildingConfig = this._config.building;

		buildingConfig.sizeChange.add(this.onSizeChange);
		buildingConfig.pitch.signal.add(this.onPitchChange);
		buildingConfig.overhang_sides.signal.add(this.updateMesh);
		buildingConfig.overhang_end.signal.add(this.updateMesh);
		// buildingConfig.overhang_front.signal.add(this.updateMesh);
		// buildingConfig.overhang_back.signal.add(this.updateMesh);
		// buildingConfig.overhang_left.signal.add(this.updateMesh);
		// buildingConfig.overhang_right.signal.add(this.updateMesh);
		buildingConfig.no_walls.signal.add(this.onNoWallsChange);
		buildingConfig.single_slope.signal.add(this.onSingleSlopeChange);
	}

	private onWainscotChange = (value:number) =>
	{
		this.updateMesh();
		this.updateCutsForWalls();
	};

	private createMeshes(config: any)
	{
		this._materials = MaterialManager.getInstance(config);
		this._geometry  = new BuildingGeometry();

		// floor

		var floorMaterial = this._materials.floor;

		var floor_geometry = BuildingGeometry.createPlaneGeometry();
		const floor = new Mesh(floor_geometry, floorMaterial);
		floor.name = "floor";
		floor.rotation.x = Math.PI/2;
		floor.position.y = 0.01;

		this._meshes = {
			floor: floor
		};
	}

	// ======================================================================================================
	// add to / remove from scene

	/**
	 * This only gets called once, initially.
	 * @param scene3D
	 */
	public addToScene(scene3D:Object3D, config: any)
	{
		this._scene3D = scene3D;
		this.init(config);

		this._roof.add(this._scene3D);
		this._wall.add(this._scene3D);

		var meshes = this._meshes;

		scene3D.add(meshes.floor);

		this._supportBeam.addToScene(this._scene3D);

		this.updateMesh();
	}

	/**
	 * Gets called when a new project is loaded and this building is removed
	 * @param scene
	 */
	public removeFromScene(scene?: Object3D)
	{
		var scene3D = scene || this._scene3D;
		this._scene3D = null;

		var meshes = this._meshes;

		this._wall.remove();

		scene3D.remove(meshes.floor);
	}

	// ======================================================================================================
	// update

	// updates the original mesh
	private updateMesh = () =>
	{
		var metrics = this.config.metrics;

		this._roof.update();
		this._wall.update();

		this._wainscot.updateSize();

		var floor = this._meshes.floor;
		floor.scale.x = metrics.width;
		floor.scale.y = metrics.length;

		Main.getInstance().render = true;
	}

	private createTrims()
	{
		this._trim = new BuildingTrim(this);
	}

	// ===========================================================================================================
	// Extras

	private createExtras()
	{
		this._ridgeVent = new RidgeVent(this);

		this._wainscot = new Wainscot(this);
		this._config.building.wainscot.signal.add(this.onWainscotChange);

		this._concreteSlab = new ConcreteSlab(this);

		this._insetBayManager = new InsetBayManager(this);
		this._leanToManager = new LeanToManager(this);
		this._supportBeam = new SupportBeam(this);
	}

	// ===========================================================================================================
	// Add/remove nodes ------------------------------------------------------------------------------------------

	/**
	 * Called when a node is permanently added on the building.
	 * todo revise casting/instanceof
	 * @param node
	 */
	public addNode(node:ISceneAddon)
	{
		if (node.type === SceneAddonType.WALL)
		{
			this.addNodeOnWall(<any>node);
		}
	}

	/**
	 * This cuts a whole in the appropriate wall.
	 * @param node
	 */
	private addNodeOnWall(node:WallAddon)
	{
		this._addons[node.parentId].push(<any>node);

		this._wall.addNode(node);
		this._wainscot.addNode(node);

		this.signals.addonsChange.dispatch(this);
	}

	public removeNode(node:ISceneAddon)
	{
		if (node.type === SceneAddonType.WALL)
		{
			this.removeFromWall(<any>node);
		}

		this.signals.addonsChange.dispatch(this);
	}

	private removeFromWall(node:WallAddon)
	{
		var wallId = node.parentId;

		// remove from addons array
		var addons = this._addons[wallId];
		var nodeIndex = addons.indexOf(<any>node);
		if (nodeIndex > -1)
		{
			addons.splice(nodeIndex, 1);
		}

		this.updateCutsForWalls();
	}

	public async setAddons(addons:IAddonSetting[])
	{
		this._scene.destroyAddons();
		let isThereInvalidPosition = false;

		for (var i = 0, ln = addons.length; i < ln; ++i)
		{
			var addonConfig = addons[i];
			var addon = AddonManager.getInstance().createAddon(addonConfig.itemId, addonConfig.position.wall);

			const isItRud = addon.itemId.includes("rollup");

			const condition1 = !(addon.setPosition(addonConfig.position));
			const condition2 = (isItRud && !this._scene.isAddonPlaceValid(addon));

			if (condition1 || condition2)
			{
				isThereInvalidPosition = true;
				console.log('The following add-on has an invalid position:');
				console.log(addon);
				// invalid position, we don't put the addon there
			}
			else
			{
				addon.show();
				// this is needed otherwise it wont be said which causes problems in BuildingController
				addon.hasValidPlace = true;
				this._scene.startPlacing(addon);
				await this._scene.addPlacingAddon(true, false);
			}
		}

		if (isThereInvalidPosition)
		{
			if (!Main.getInstance().authentication.isCurrentProjectInitial())
			{
				await WarningWindow.open(`One or more components has been removed because of an invalid position.\nPlease add any components to a valid position and save your project.`);
			}
		}
	}

	public get leanToManager()
    {
        return this._leanToManager;
    }
	// ===========================================================================================================
	// Update CSG ------------------------------------------------------------------------------------------------

	/**
	 * Updates wall cuts (with CSG).
	 * This need to be called in these cases:
	 * - size is changed (widht, height, length) -> UV's get invalid
	 * - a node is removed (not needed when a node is added)
	 *
	 * This function takes the original walls and regenerates the cut out walls with threeCSG.
	 */
	private updateCutsForWalls()
	{
		this._wall.updateCuts(this._addons);
		this._wainscot.updateCuts(this._addons);

		// TODO
		//this._supportBeam.onUpdate();
	}

	// ===========================================================================================================
	// Size, pitch, overhang change

	private onSizeChange = ():void =>
	{
		// updates the original meshes
		this.updateMesh();
		// updates current meshes with the current holes
		this.updateCutsForWalls();
	};

	private onPitchChange = (value:number) =>
	{
		this.updateMesh();
		this.updateCutsForWalls();
		/*setTimeout(function()
		{
			Main.getInstance().render = true;
		},100);*/

		requestAnimationFrame(() =>
		{
			Main.getInstance().render = true;
		});
		/*var building = Main.getInstance().config.building;
		setTimeout(()=>
		{
			building.width.setValue(building.width.value + 1);
			building.width.setValue(building.width.value - 1);
		}, 1);*/

	};

	private onNoWallsChange = (value:boolean) =>
	{
		this._wall.setHidden(value);
		this._meshes.floor.visible = !value;
		// concrete slab will adjust itself automatically to cover the floor

		// hide all addons

		for (var wallId in this._addons)
		{
			var addons = this._addons[wallId];
			for (var i = 0; i < addons.length; i++)
			{
				if (value)
				{
					addons[i].hide();
				}
				else
				{
					addons[i].show();
				}
			}
		}
	}

	private onSingleSlopeChange = (value:string) =>
	{
		this.updateMesh();
	}

	// ===========================================================================================================
	// Scene, config, materials

	public get scene3D()
	{
		return this._scene3D;
	}

	public get scene()
	{
		return this._scene;
	}

	public get config()
	{
		return this._config;
	}

	public get materials()
	{
		return this._materials;
	}

	public get supportBeam()
	{
		return this._supportBeam;
	}

	public get insetBaymanager()
	{
		return this._insetBayManager;
	}

	public get concreteSlab()
	{
		return this._concreteSlab;
	}

	// ===========================================================================================================
	// Dispose

	public get addons()
	{
		return this._addons;
	}

	public get wall()
	{
		return this._wall;
	}

	public dispose()
	{

	}
}